import {
  Box,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoSearch } from 'react-icons/io5';
import { AiOutlineMessage } from 'react-icons/ai';
import { FiUser, FiUsers } from 'react-icons/fi';
import { HiOutlineUserGroup } from 'react-icons/hi';
import { RiAddCircleLine } from 'react-icons/ri';
import {
  ConversationType,
  useConversationsQuery,
} from '../../generated/graphql';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanLoading } from '../HelivanLoading';
import { HelivanButton } from '../helivan/HelivanButton';
import { CreateConversationModal } from './CreateConversationModal';
import { formatDateShort } from '../../utils/formatDate';

export const Conversations = () => {
  const { data, loading } = useConversationsQuery();
  const conversations = data?.getAllConversations ?? [];
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | ConversationType>('all');

  // Filter conversations based on search query and type
  const filteredConversations = useMemo(() => {
    let filtered = conversations;

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter((c) => c.type === filterType);
    }

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (conversation) =>
          conversation.title.toLowerCase().includes(query) ||
          (conversation.digitalTwins &&
            conversation.digitalTwins.some((dt) =>
              dt.name.toLowerCase().includes(query)
            ))
      );
    }

    return filtered;
  }, [conversations, searchQuery, filterType]);

  const createConversationModal = useDisclosure();
  const navigate = useNavigate();

  return loading ? (
    <HelivanLoading />
  ) : (
    <Stack width="full" height="full" spacing={0} overflow="hidden">
      {/* Header - full width */}
      <Flex
        width="full"
        alignItems="center"
        py="12px"
        px="24px"
        bg={HelivanColor.background.primary}
        borderBottom="1px solid"
        borderBottomColor={HelivanColor.border.primary}
        justifyContent="space-between"
        gap={4}
        flexShrink={0}
      >
        {/* Left: Title and view toggles */}
        <HStack spacing={6} flex="0 0 auto">
          <HelivanText variant="sectionHeader">Conversations</HelivanText>
          <HStack spacing={2}>
            <Box
              onClick={() => setFilterType('all')}
              px="8px"
              py="4px"
              bg={filterType === 'all' ? HelivanColor.background.secondary : 'white'}
              border="1px solid"
              borderColor={HelivanColor.border.primary}
              borderRadius="6px"
              _hover={{ bg: HelivanColor.background.secondary }}
              cursor="pointer"
            >
              <HStack spacing={2}>
                <AiOutlineMessage size={14} color={HelivanColor.text.blueMid} />
                <HelivanText variant="smallBody">All</HelivanText>
              </HStack>
            </Box>
            <Box
              onClick={() => setFilterType(ConversationType.Persona)}
              px="8px"
              py="4px"
              bg={filterType === ConversationType.Persona ? HelivanColor.background.secondary : 'white'}
              border="1px solid"
              borderColor={HelivanColor.border.primary}
              borderRadius="6px"
              _hover={{ bg: HelivanColor.background.secondary }}
              cursor="pointer"
            >
              <HelivanText variant="smallBody">1-1</HelivanText>
            </Box>
            <Box
              onClick={() => setFilterType(ConversationType.Panel)}
              px="8px"
              py="4px"
              bg={filterType === ConversationType.Panel ? HelivanColor.background.secondary : 'white'}
              border="1px solid"
              borderColor={HelivanColor.border.primary}
              borderRadius="6px"
              _hover={{ bg: HelivanColor.background.secondary }}
              cursor="pointer"
            >
              <HelivanText variant="smallBody">Panel</HelivanText>
            </Box>
            <Box
              onClick={() => setFilterType(ConversationType.Demographic)}
              px="8px"
              py="4px"
              bg={filterType === ConversationType.Demographic ? HelivanColor.background.secondary : 'white'}
              border="1px solid"
              borderColor={HelivanColor.border.primary}
              borderRadius="6px"
              _hover={{ bg: HelivanColor.background.secondary }}
              cursor="pointer"
            >
              <HelivanText variant="smallBody">Crowd</HelivanText>
            </Box>
          </HStack>
        </HStack>

        {/* Center: Search bar */}
        <Box flex="1" maxWidth="400px">
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none">
              <IoSearch size={16} color={HelivanColor.text.blueMid} />
            </InputLeftElement>
            <Input
              bg={HelivanColor.background.secondary}
              borderRadius="6px"
              border="none"
              placeholder="Search conversations..."
              _placeholder={{ color: HelivanColor.text.blueMid }}
              textColor="black"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>
        </Box>

        {/* Right: Create button */}
        <HStack spacing={3} flex="0 0 auto">
          <HelivanButton
            variant="green"
            size="sm"
            onClick={createConversationModal.onOpen}
          >
            <HStack spacing={1}>
              <RiAddCircleLine color="white" size={14} />
              <HelivanText colorOverride="white" variant="smallBody">
                New Conversation
              </HelivanText>
            </HStack>
          </HelivanButton>
        </HStack>
      </Flex>

      <CreateConversationModal
        isOpen={createConversationModal.isOpen}
        onClose={createConversationModal.onClose}
      />

      {/* Content area - separate from header */}
      <Flex
        flex="1"
        overflowY="auto"
        bg={HelivanColor.background.secondary}
        p={5}
        justifyContent="center"
      >
        {conversations.length > 0 ? (
          filteredConversations.length > 0 ? (
            <Stack
              spacing={0}
              width="full"
              maxWidth="640px"
              bg="white"
              borderRadius="8px"
              border="1px solid"
              borderColor={HelivanColor.border.primary}
              overflow="hidden"
              height="fit-content"
            >
              {filteredConversations.map((conversation, index) => (
                <ConversationRow
                  key={conversation.id}
                  id={conversation.id}
                  title={conversation.title}
                  createdAt={conversation.updatedAt ?? ''}
                  dtName={
                    conversation.type === ConversationType.Demographic
                      ? (conversation.demographic ?? 'Conversation')
                      : conversation.digitalTwins.length > 0
                        ? conversation.digitalTwins
                            .map((twin) => twin.name)
                            .join(', ')
                        : 'Conversation'
                  }
                  type={conversation.type}
                  isLast={index === filteredConversations.length - 1}
                />
              ))}
            </Stack>
          ) : (
            <Flex justifyContent="center" alignItems="center" width="full">
              <HelivanText variant="largeBody">
                {searchQuery.trim()
                  ? 'No matching conversations found'
                  : 'No conversations available'}
              </HelivanText>
            </Flex>
          )
        ) : (
          <Flex
            width="full"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            gap="12px"
          >
            <Text fontSize="14px" color={HelivanColor.text.blueMid}>
              Your chat history will appear here.
            </Text>
            <Text fontSize="14px" color={HelivanColor.text.blueMid}>
              Navigate to{' '}
              <Link
                onClick={() => navigate('/projects')}
                fontWeight={600}
                cursor="pointer"
              >
                Watches
              </Link>{' '}
              to get started.
            </Text>
          </Flex>
        )}
      </Flex>
    </Stack>
  );
};

const ConversationRow = ({
  id,
  title,
  createdAt,
  dtName,
  type,
  isLast,
}: {
  id: string;
  title: string;
  createdAt: string;
  dtName: string;
  type: ConversationType;
  isLast: boolean;
}) => {
  const navigate = useNavigate();

  const getTypeIcon = () => {
    switch (type) {
      case ConversationType.Persona:
        return FiUser;
      case ConversationType.Panel:
        return FiUsers;
      case ConversationType.Demographic:
        return HiOutlineUserGroup;
      default:
        return AiOutlineMessage;
    }
  };

  const getTypeLabel = () => {
    switch (type) {
      case ConversationType.Persona:
        return '1-1';
      case ConversationType.Panel:
        return 'Panel';
      case ConversationType.Demographic:
        return 'Crowd';
      default:
        return 'Chat';
    }
  };

  const TypeIcon = getTypeIcon();

  return (
    <Flex
      alignItems="center"
      gap={3}
      py={2.5}
      px={3}
      borderBottom={isLast ? 'none' : '1px solid'}
      borderBottomColor={HelivanColor.border.primary}
      _hover={{
        cursor: 'pointer',
        bg: HelivanColor.background.secondary,
      }}
      transition="background 0.15s"
      onClick={() => navigate(`/conversations/${id}`)}
      bg="white"
    >
      {/* Type icon in fixed container */}
      <Flex
        alignItems="center"
        justifyContent="center"
        width="28px"
        height="28px"
        borderRadius="6px"
        bg={HelivanColor.background.secondary}
        flexShrink={0}
      >
        <TypeIcon size={14} color={HelivanColor.text.helivanBlue} />
      </Flex>

      {/* Title and metadata */}
      <Stack spacing={0} flex={1} minWidth={0}>
        <Text
          fontSize="13px"
          fontWeight={500}
          color={HelivanColor.text.blueDark}
          noOfLines={1}
        >
          {title}
        </Text>
        <HStack spacing={1}>
          <Text fontSize="11px" color={HelivanColor.text.blueMid} noOfLines={1}>
            {dtName}
          </Text>
          <Text fontSize="11px" color={HelivanColor.text.gray}>·</Text>
          <Text fontSize="11px" color={HelivanColor.text.gray}>
            {getTypeLabel()}
          </Text>
        </HStack>
      </Stack>

      {/* Timestamp */}
      <Text fontSize="11px" color={HelivanColor.text.gray} flexShrink={0}>
        {formatDateShort(createdAt)}
      </Text>
    </Flex>
  );
};
